import { MonthAndYear, useActiveDate, useGoodlokAuth } from '@goodlok/react-sdk'
import { useQuery } from '@tanstack/react-query'
import { DeadlineTemplateFragment } from '../app/data/content/DeadlineTemplateFragment'
import { EmployeeRoleFragment } from '../app/data/content/EmployeeRoleFragment'
import { ShiftsProfileFragment } from '../app/data/content/ShiftsProfileFragment'
import { TimetableShiftFragment } from '../app/data/content/TimetableShiftFragment'
import { UnassignReasonFragment } from '../app/data/content/UnassignReasonFragment'
import { useRoleIds } from '../app/utils/useRoleIds'
import { useVenue } from '../components/Head'
import { adminZeus } from '../components/Layout'
import { useVenues } from './useVenues'

const getMonthStart = (date: MonthAndYear) => {
	const newDate = new Date(Date.UTC(date.year, date.month, 1))
	while (newDate.getUTCDay() !== 1) {
		newDate.setUTCDate(newDate.getUTCDate() - 1)
	}
	return newDate
}

const getMonthEnd = (date: MonthAndYear) => {
	const newDate = new Date(Date.UTC(date.year, date.month + 1, 0))
	while (newDate.getUTCDay() !== 0) {
		newDate.setUTCDate(newDate.getUTCDate() + 1)
	}
	return newDate
}

export const useAdminPageData = () => {
	const auth = useGoodlokAuth()
	const { staffId } = auth
	const { activeDate } = useActiveDate()
	const dateStart = getMonthStart(activeDate)
	const dateEnd = getMonthEnd(activeDate)

	const [venueId, setVenueId] = useVenue()
	const [roleIds] = useRoleIds()
	const venues = useVenues()

	return useQuery(
		['adminPageData', staffId, roleIds, venues, venueId, dateStart, dateEnd] as const,
		async (q) => {
			const staffId = q.queryKey[1]

			if (!dateStart || !dateEnd || typeof staffId !== 'string' || !venues.data) {
				return null
			}

			if (venues.data.length === 0 || venues.data[0].employeeRoles.length === 0) {
				// notify(
				// 	'Není vybraná pobočka nebo zaměstanecká role, kontaktujte manažera pobočky.',
				// 	'error'
				// )
				return null
			}
			// ⬇️ @TODO this should probably be handled somewhere else
			if (!venueId) {
				setVenueId(venues.data[0].id)
			}
			// ⬆️

			const data = await adminZeus('query')({
				getVenue: [
					{ by: { id: venueId ?? venues.data[0].id } },
					{
						id: true,
						name: true,
						timetableDays: [
							{
								filter: {
									date: {
										gte: dateStart.toISOString(),
										lte: dateEnd.toISOString(),
									},
								},
								orderBy: [{ date: 'asc' }],
							},
							{
								id: true,
								date: true,
								tipsCents: true,
								type: true,
								shiftGroups: [
									{
										filter: {
											type: {
												id: {
													in: roleIds,
												},
											},
										},
										orderBy: [
											{ type: { name: 'asc' } },
											{ position: { start: 'asc' } },
											{ position: { end: 'asc' } },
										],
									},
									{
										id: true,
										day: [
											{},
											{
												date: true,
											},
										],
										type: [
											{},
											{
												id: true,
												name: true,
												color: true,
											},
										],
										position: [
											{},
											{
												id: true,
												name: true,
												start: true,
												end: true,
												isDynamic: true,
											},
										],
										shifts: [
											{
												orderBy: [{ assigned: 'asc' }],
											},
											TimetableShiftFragment(),
										],
										queue: [
											{},
											{
												id: true,
												//PROBBABLY REDUNDANT BUT CHECK BEFORE DELETING
												shiftGroup: [
													{},
													{
														day: [
															{},
															{
																date: true,
															},
														],
													},
												],
												//
												items: [
													{
														orderBy: [
															{ shift: { assigned: 'asc' } },
															{ shiftsProfile: { staff: { user: { name: 'asc' } } } },
														],
													},
													{
														id: true,
														shiftsProfile: [{}, ShiftsProfileFragment()],
														start: true,
														end: true,
														queue: [{}, { shiftGroup: [{}, { day: [{}, { date: true }] }] }],
														unassignInfo: [
															{},
															{
																reason: [
																	{},
																	{
																		id: true,
																		name: true,
																	},
																],
																hoursMissed: true,
															},
														],
														shift: [
															{},
															{
																id: true,
																locked: true,
																isOvertime: true,
															},
														],
													},
												],
											},
										],
									},
								],
								notes: [
									{ orderBy: [{ order: 'asc' }] },
									{
										id: true,
										note: true,
										role: [{}, EmployeeRoleFragment()],
									},
								],
							},
						],
						deadlineTemplate: [{}, DeadlineTemplateFragment()],
						employeeRoles: [
							{ orderBy: [{ role: { name: 'asc' } }] },
							{
								role: [
									{},
									{
										...EmployeeRoleFragment(),
										shiftsProfiles: [
											{ filter: { staff: { venues: { id: { eq: venueId } } } } },
											{
												id: true,
												staff: [
													{},
													{
														id: true,
														user: [{}, { name: true, email: true }],
														shiftsProfile: [{}, { id: true }],
													},
												],
												queueItems: [
													{},
													{
														queue: [
															{},
															{
																shiftGroup: [
																	{},
																	{
																		id: true,
																	},
																],
															},
														],
													},
												],
												shifts: [
													{},
													{
														id: true,
														queueItem: [
															{},
															{
																id: true,
																start: true,
																end: true,
																unassignInfo: [
																	{},
																	{
																		reason: [
																			{},
																			{
																				id: true,
																				name: true,
																			},
																		],
																		hoursMissed: true,
																	},
																],
															},
														],
														group: [
															{},
															{
																id: true,

																day: [
																	{},
																	{
																		date: true,
																	},
																],
															},
														],
													},
												],
											},
										],
									},
								],
							},
						],
						enabledUnassignReasons: [{ orderBy: [{ order: 'asc' }] }, UnassignReasonFragment()],
					},
				],
				getStaff: [
					{ by: { id: staffId } },
					{
						id: true,
						user: [{}, { name: true, email: true }],
					},
				],
				listTimetableEmployeeRole: [{ orderBy: [{ name: 'asc' }] }, EmployeeRoleFragment()],
			})

			return { ...data }
		},
		{
			keepPreviousData: true,
			refetchInterval: parseInt(process.env.REFETCH_INTERVAL_MILLISECONDS ?? '10000'),
		}
	)
}

export type AdminPageData = NonNullable<ReturnType<typeof useAdminPageData>['data']>
